import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { COMPANY_STYLES } from "@/api";
import { useAuth } from "@/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { fmtDateTime } from "@/api";

const MODULES_LIST = ["leads", "quotations", "jobs", "billing", "stock", "leaves", "users", "settings", "reports", "notifications", "money", "purchase", "expenses", "payroll", "customers", "vendors", "catalog", "jobs_orders", "amc_warranty", "tasks_reminders", "documents", "feedback", "recycle_bin", "help_support", "audit_logs"];
const ACTIONS_LIST = ["view", "create", "edit", "approve", "delete", "export"];
const MORE_KEYS = ["myhr", "customers", "vendors", "catalog", "jobs_orders", "amc_warranty", "tasks_reminders", "documents", "reports", "feedback", "notifications", "audit_logs", "recycle_bin", "help_support"];

export default function Settings() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [settings, setSettings] = useState(null);
  const [roles, setRoles] = useState(null);
  const [audit, setAudit] = useState([]);
  const [workspaceConfig, setWorkspaceConfig] = useState(null);
  const [workspaceTenantId, setWorkspaceTenantId] = useState(null);
  const [attendanceUsers, setAttendanceUsers] = useState([]);
  const [roleName, setRoleName] = useState("");
  const [perms, setPerms] = useState({});
  const isOwner = user.role === "super_admin";
  const canManageModules = isOwner || user.is_workspace_owner || (user.permissions?.settings || []).includes("edit");
  const canManageAttendance = isOwner || user.is_workspace_owner;
  const canViewAudit = isOwner || user.is_workspace_owner || (user.permissions?.audit_logs || []).includes("view");

  const load = useCallback(() => {
    api.get("/companies").then((r) => setCompanies(r.data));
    api.get("/settings").then((r) => setSettings(r.data));
    if (canManageModules) api.get("/workspace/config").then((r) => {
      setWorkspaceTenantId(r.data.tenant?.id);
      setWorkspaceConfig(r.data.config);
    }).catch(() => {});
    if (canManageAttendance) api.get("/users").then((r) => setAttendanceUsers(r.data)).catch(() => {});
    if (isOwner) {
      api.get("/roles").then((r) => setRoles(r.data)).catch(() => {});
    }
    if (canViewAudit) {
      api.get("/audit-logs").then((r) => setAudit(r.data)).catch(() => {});
    }
  }, [canManageAttendance, canManageModules, canViewAudit, isOwner]);
  useEffect(() => { load(); }, [load]);

  const togglePerm = (m, a) => {
    const cur = perms[m] || [];
    setPerms({ ...perms, [m]: cur.includes(a) ? cur.filter((x) => x !== a) : [...cur, a] });
  };

  const createRole = async () => {
    try {
      await api.post("/roles", { name: roleName, permissions: perms });
      toast.success(`Role "${roleName}" created`);
      setRoleName(""); setPerms({}); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const saveWorkspaceConfig = async (section, value) => {
    try {
      const { data } = await api.patch("/workspace/config", { section, value });
      setWorkspaceConfig(data);
      window.dispatchEvent(new CustomEvent("workspace-config-updated", { detail: data }));
      toast.success("Module settings saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save module settings"); }
  };

  const updateMoreModule = (key, changes) => setWorkspaceConfig((current) => ({
    ...current,
    modules: current.modules.map((module) => module.key === key ? { ...module, ...changes } : module),
  }));

  const moveMoreModule = (key, direction) => {
    const ordered = workspaceConfig.modules.filter((module) => MORE_KEYS.includes(module.key)).sort((a, b) => a.more_order - b.more_order);
    const index = ordered.findIndex((module) => module.key === key);
    const target = index + direction;
    if (target < 0 || target >= ordered.length) return;
    [ordered[index], ordered[target]] = [ordered[target], ordered[index]];
    const orders = Object.fromEntries(ordered.map((module, position) => [module.key, position]));
    setWorkspaceConfig((current) => ({ ...current, modules: current.modules.map((module) => MORE_KEYS.includes(module.key) ? { ...module, more_order: orders[module.key] } : module) }));
  };

  const attendanceSettings = workspaceConfig?.attendance || {
    gate_mode: "strict", shift_start: "09:30", shift_end: "18:30", grace_minutes: 15,
    gps_enabled: false, selfie_enabled: false, geofence_radius_m: 200,
    geofence_center: { lat: "", lng: "" }, allow_breaks: false, exempt_user_ids: [],
  };
  const updateAttendance = (field, value) => setWorkspaceConfig((current) => ({
    ...current,
    attendance: { ...attendanceSettings, [field]: value },
  }));
  const saveAttendance = () => saveWorkspaceConfig("attendance", {
    ...attendanceSettings,
    grace_minutes: Number(attendanceSettings.grace_minutes || 0),
    geofence_radius_m: Number(attendanceSettings.geofence_radius_m || 0),
    geofence_center: {
      lat: attendanceSettings.geofence_center?.lat === "" ? null : Number(attendanceSettings.geofence_center?.lat),
      lng: attendanceSettings.geofence_center?.lng === "" ? null : Number(attendanceSettings.geofence_center?.lng),
    },
    exempt_user_ids: attendanceSettings.exempt_user_ids || [],
  });

  const saveCompany = async (c) => {
    try {
      await api.patch(`/companies/${c.code}`, { gst_rate: parseFloat(c.gst_rate), commission_pct: parseFloat(c.commission_pct), travel_allowance: parseFloat(c.travel_allowance), recurring: !!c.recurring });
      toast.success(`${c.name} rates saved`);
    } catch (err) { toast.error(err.response?.data?.detail || "Only Owner can edit rates"); }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      await api.patch("/settings", {
        geofence_radius_m: parseFloat(settings.geofence_radius_m), sla_hours: parseFloat(settings.sla_hours),
        incentive_per_job: parseFloat(settings.incentive_per_job), incentive_rating_threshold: parseFloat(settings.incentive_rating_threshold),
        incentive_rating_bonus: parseFloat(settings.incentive_rating_bonus), overdue_days: parseInt(settings.overdue_days),
        recurring_interval_days: parseInt(settings.recurring_interval_days),
        working_hours_start: settings.working_hours_start, working_hours_end: settings.working_hours_end,
        min_gap_minutes: parseFloat(settings.min_gap_minutes), max_jobs_per_day: parseInt(settings.max_jobs_per_day),
        customer_reminder_hours: String(settings.customer_reminder_hours).split(",").map((x) => parseFloat(x.trim())).filter((x) => !isNaN(x)),
        team_reminder_hours: String(settings.team_reminder_hours).split(",").map((x) => parseFloat(x.trim())).filter((x) => !isNaN(x)),
        admin_digest_enabled: !!settings.admin_digest_enabled,
        customer_reminder_template: settings.customer_reminder_template,
        team_reminder_template: settings.team_reminder_template,
      });
      toast.success("Settings saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Only Owner can edit settings"); }
  };

  if (!settings) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight">Settings & Rate Cards</h1>
        <p className="text-sm text-muted-foreground">{isOwner ? "All rates editable — no code changes needed" : "View only — only the Owner can edit rates"}</p>
      </div>

      <Tabs defaultValue="rates" data-testid="settings-tabs">
        <TabsList className="mb-4">
          <TabsTrigger value="rates" data-testid="tab-rates">Rates & Rules</TabsTrigger>
          {canManageAttendance && <TabsTrigger value="attendance" data-testid="tab-attendance-settings">Attendance</TabsTrigger>}
          {canManageModules && <TabsTrigger value="modules" data-testid="tab-modules">Modules</TabsTrigger>}
          {isOwner && <TabsTrigger value="roles" data-testid="tab-roles">Roles & Permissions</TabsTrigger>}
          {canViewAudit && <TabsTrigger value="audit" data-testid="tab-audit">Audit Log</TabsTrigger>}
        </TabsList>
        <TabsContent value="rates" className="space-y-6">
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {companies.map((c) => (
          <Card key={c.code} className="p-5 space-y-3" data-testid={`company-card-${c.code}`}>
            <div className="flex items-center justify-between">
              <span className={`text-xs px-2 py-1 rounded-full border ${COMPANY_STYLES[c.name] || ""}`}>{c.name}</span>
              <span className="text-xs text-muted-foreground">{c.label}</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-[11px] uppercase tracking-wide text-muted-foreground">GST %</label>
                <Input data-testid={`gst-input-${c.code}`} type="number" step="0.1" value={c.gst_rate} disabled={!isOwner}
                  onChange={(e) => setCompanies(companies.map((x) => x.code === c.code ? { ...x, gst_rate: e.target.value } : x))} className="mt-1 h-9" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Comm %</label>
                <Input data-testid={`comm-input-${c.code}`} type="number" step="0.1" value={c.commission_pct} disabled={!isOwner}
                  onChange={(e) => setCompanies(companies.map((x) => x.code === c.code ? { ...x, commission_pct: e.target.value } : x))} className="mt-1 h-9" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Travel ₹</label>
                <Input data-testid={`travel-input-${c.code}`} type="number" step="1" value={c.travel_allowance} disabled={!isOwner}
                  onChange={(e) => setCompanies(companies.map((x) => x.code === c.code ? { ...x, travel_allowance: e.target.value } : x))} className="mt-1 h-9" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid={`recurring-${c.code}`} checked={!!c.recurring} disabled={!isOwner}
                onChange={(e) => setCompanies(companies.map((x) => x.code === c.code ? { ...x, recurring: e.target.checked } : x))} />
              Recurring service brand (monthly re-booking reminders)
            </label>
            {isOwner && <Button size="sm" data-testid={`save-company-${c.code}`} onClick={() => saveCompany(c)} className="w-full bg-primary hover:bg-primary-hover h-9">Save {c.code} Rates</Button>}
          </Card>
        ))}
      </div>

      <Card className="p-5" data-testid="global-settings-card">
        <h3 className="font-heading font-semibold mb-4">Automation & Incentive Rules</h3>
        <form onSubmit={saveSettings} className="grid md:grid-cols-3 gap-4">
          {[
            ["geofence_radius_m", "Geofence radius (m)"],
            ["sla_hours", "Lead call SLA (hours)"],
            ["incentive_per_job", "Incentive per job (₹)"],
            ["incentive_rating_threshold", "Rating bonus threshold"],
            ["incentive_rating_bonus", "Rating bonus (₹)"],
            ["recurring_interval_days", "Recurring reminder (days)"],
          ].map(([key, label]) => (
            <div key={key}>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</label>
              <Input data-testid={`setting-${key}`} type="number" step="0.1" value={settings[key] ?? ""} disabled={!isOwner}
                onChange={(e) => setSettings({ ...settings, [key]: e.target.value })} className="mt-1" />
            </div>
          ))}
          {isOwner && <div className="md:col-span-3"><Button data-testid="save-settings-button" type="submit" className="bg-primary hover:bg-primary-hover">Save Automation Rules</Button></div>}
        </form>
      </Card>

      <Card className="p-5" data-testid="scheduling-settings-card">
        <h3 className="font-heading font-semibold mb-4">Scheduling & Booking Reminders</h3>
        <form onSubmit={saveSettings} className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Working hours start</label>
            <Input data-testid="setting-wh-start" type="time" value={settings.working_hours_start || "09:00"} disabled={!isOwner}
              onChange={(e) => setSettings({ ...settings, working_hours_start: e.target.value })} className="mt-1" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Working hours end</label>
            <Input data-testid="setting-wh-end" type="time" value={settings.working_hours_end || "18:00"} disabled={!isOwner}
              onChange={(e) => setSettings({ ...settings, working_hours_end: e.target.value })} className="mt-1" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Min gap between jobs (min)</label>
            <Input data-testid="setting-min-gap" type="number" value={settings.min_gap_minutes ?? 60} disabled={!isOwner}
              onChange={(e) => setSettings({ ...settings, min_gap_minutes: e.target.value })} className="mt-1" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Max jobs per team per day</label>
            <Input data-testid="setting-max-jobs" type="number" value={settings.max_jobs_per_day ?? 3} disabled={!isOwner}
              onChange={(e) => setSettings({ ...settings, max_jobs_per_day: e.target.value })} className="mt-1" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Customer reminders (hours before, comma-sep)</label>
            <Input data-testid="setting-cust-reminders" value={Array.isArray(settings.customer_reminder_hours) ? settings.customer_reminder_hours.join(", ") : settings.customer_reminder_hours || "24, 2"} disabled={!isOwner}
              onChange={(e) => setSettings({ ...settings, customer_reminder_hours: e.target.value })} className="mt-1" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Team reminders (hours before)</label>
            <Input data-testid="setting-team-reminders" value={Array.isArray(settings.team_reminder_hours) ? settings.team_reminder_hours.join(", ") : settings.team_reminder_hours || "1"} disabled={!isOwner}
              onChange={(e) => setSettings({ ...settings, team_reminder_hours: e.target.value })} className="mt-1" />
          </div>
          <label className="flex items-center gap-2 text-sm md:col-span-3">
            <input type="checkbox" data-testid="setting-admin-digest" checked={!!settings.admin_digest_enabled} disabled={!isOwner}
              onChange={(e) => setSettings({ ...settings, admin_digest_enabled: e.target.checked })} />
            Send Admin/Owner a daily 8 AM digest of the day's full schedule
          </label>
          <div className="md:col-span-3">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Customer reminder template</label>
            <textarea data-testid="setting-cust-template" value={settings.customer_reminder_template || ""} disabled={!isOwner}
              onChange={(e) => setSettings({ ...settings, customer_reminder_template: e.target.value })}
              className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm min-h-[70px]" />
            <div className="text-[11px] text-muted-foreground mt-1">Placeholders: {"{customer_name} {date} {time} {team_leader_name} {company_name} {address} {job_no}"}</div>
          </div>
          <div className="md:col-span-3">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Team reminder template</label>
            <textarea data-testid="setting-team-template" value={settings.team_reminder_template || ""} disabled={!isOwner}
              onChange={(e) => setSettings({ ...settings, team_reminder_template: e.target.value })}
              className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm min-h-[70px]" />
          </div>
          {isOwner && <div className="md:col-span-3"><Button data-testid="save-scheduling-button" type="submit" className="bg-primary hover:bg-primary-hover">Save Scheduling Rules</Button></div>}
        </form>
      </Card>
        </TabsContent>

        {canManageAttendance && (
          <TabsContent value="attendance">
            {!workspaceConfig ? <div className="text-sm text-muted-foreground">Loading attendance settings…</div> : (
              <Card className="p-5 space-y-5" data-testid="attendance-settings-panel">
                <div>
                  <h2 className="font-heading font-semibold">Attendance rules</h2>
                  <p className="text-sm text-muted-foreground">Applies only to this workspace. Owners are always exempt.</p>
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <div>
                    <label className="text-[11px] uppercase tracking-wide text-muted-foreground" htmlFor="attendance-gate-mode">Gate mode</label>
                    <select id="attendance-gate-mode" data-testid="attendance-gate-mode" value={attendanceSettings.gate_mode || "strict"}
                      onChange={(event) => updateAttendance("gate_mode", event.target.value)} className="mt-1 h-10 w-full rounded-md border bg-background px-3 text-sm">
                      <option value="strict">Strict</option><option value="soft">Soft reminder</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-wide text-muted-foreground" htmlFor="attendance-shift-start">Shift starts</label>
                    <Input id="attendance-shift-start" data-testid="attendance-shift-start" type="time" value={attendanceSettings.shift_start || "09:30"}
                      onChange={(event) => updateAttendance("shift_start", event.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-wide text-muted-foreground" htmlFor="attendance-shift-end">Shift ends</label>
                    <Input id="attendance-shift-end" data-testid="attendance-shift-end" type="time" value={attendanceSettings.shift_end || "18:30"}
                      onChange={(event) => updateAttendance("shift_end", event.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-wide text-muted-foreground" htmlFor="attendance-grace-minutes">Grace minutes</label>
                    <Input id="attendance-grace-minutes" data-testid="attendance-grace-minutes" type="number" min="0" value={attendanceSettings.grace_minutes ?? 15}
                      onChange={(event) => updateAttendance("grace_minutes", event.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-wide text-muted-foreground" htmlFor="attendance-geofence-radius">Geofence radius (m)</label>
                    <Input id="attendance-geofence-radius" data-testid="attendance-geofence-radius" type="number" min="0" value={attendanceSettings.geofence_radius_m ?? 200}
                      onChange={(event) => updateAttendance("geofence_radius_m", event.target.value)} className="mt-1" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[11px] uppercase tracking-wide text-muted-foreground" htmlFor="attendance-center-lat">Site latitude</label>
                      <Input id="attendance-center-lat" type="number" step="any" value={attendanceSettings.geofence_center?.lat ?? ""}
                        onChange={(event) => updateAttendance("geofence_center", { ...attendanceSettings.geofence_center, lat: event.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-wide text-muted-foreground" htmlFor="attendance-center-lng">Site longitude</label>
                      <Input id="attendance-center-lng" type="number" step="any" value={attendanceSettings.geofence_center?.lng ?? ""}
                        onChange={(event) => updateAttendance("geofence_center", { ...attendanceSettings.geofence_center, lng: event.target.value })} className="mt-1" />
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-3 border-y py-4 text-sm">
                  <label className="flex items-center gap-2"><input type="checkbox" data-testid="attendance-gps-enabled" checked={!!attendanceSettings.gps_enabled}
                    onChange={(event) => updateAttendance("gps_enabled", event.target.checked)} /> Require GPS</label>
                  <label className="flex items-center gap-2"><input type="checkbox" data-testid="attendance-selfie-enabled" checked={!!attendanceSettings.selfie_enabled}
                    onChange={(event) => updateAttendance("selfie_enabled", event.target.checked)} /> Require selfie</label>
                  <label className="flex items-center gap-2"><input type="checkbox" data-testid="attendance-allow-breaks" checked={!!attendanceSettings.allow_breaks}
                    onChange={(event) => updateAttendance("allow_breaks", event.target.checked)} /> Allow multiple sessions per day</label>
                </div>
                <div>
                  <h3 className="text-sm font-semibold">Exempt from attendance</h3>
                  <div className="mt-2 grid gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
                    {attendanceUsers.filter((employee) => employee.id !== user.id && employee.tenant_ids?.includes(workspaceTenantId)).map((employee) => (
                      <label key={employee.id} className="flex min-w-0 items-center gap-2 text-sm">
                        <input type="checkbox" checked={(attendanceSettings.exempt_user_ids || []).includes(employee.id)}
                          onChange={(event) => {
                            const current = attendanceSettings.exempt_user_ids || [];
                            updateAttendance("exempt_user_ids", event.target.checked ? [...current, employee.id] : current.filter((id) => id !== employee.id));
                          }} />
                        <span className="truncate">{employee.name}</span><span className="text-xs text-muted-foreground">{employee.custom_role || employee.role}</span>
                      </label>
                    ))}
                    {!attendanceUsers.some((employee) => employee.id !== user.id && employee.tenant_ids?.includes(workspaceTenantId)) &&
                      <p className="text-sm text-muted-foreground">No other workspace users found.</p>}
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button data-testid="save-attendance-settings" onClick={saveAttendance} className="bg-primary text-primary-foreground hover:bg-primary/90">Save Attendance Rules</Button>
                </div>
              </Card>
            )}
          </TabsContent>
        )}

        {canManageModules && (
          <TabsContent value="modules">
            {!workspaceConfig ? <div className="text-sm text-muted-foreground">Loading module settings…</div> : (
              <Card className="p-5 space-y-3" data-testid="settings-modules-panel">
                <div>
                  <h2 className="font-heading font-semibold">More navigation</h2>
                  <p className="text-sm text-muted-foreground">Changes apply to this company. The existing 16 navigation entries keep their fixed order and names.</p>
                </div>
                <div className="flex items-center gap-2 border-b pb-3 text-sm">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" data-testid="show-group-labels" checked={workspaceConfig.sidebar?.show_group_labels !== false}
                      onChange={(event) => setWorkspaceConfig({ ...workspaceConfig, sidebar: { ...workspaceConfig.sidebar, show_group_labels: event.target.checked }})} />
                    Show group labels
                  </label>
                  <Button size="sm" variant="outline" className="ml-auto" data-testid="save-group-labels"
                    onClick={() => saveWorkspaceConfig("sidebar", workspaceConfig.sidebar)}>Save</Button>
                </div>
                {workspaceConfig.modules.filter((module) => MORE_KEYS.includes(module.key)).sort((a, b) => a.more_order - b.more_order).map((module, index, list) => (
                  <div key={module.key} data-testid={`settings-more-${module.key}`} className="flex items-center gap-3 border rounded-md px-3 py-2">
                    <input type="checkbox" aria-label={`Show ${module.label}`} checked={module.enabled !== false}
                      onChange={(event) => updateMoreModule(module.key, { enabled: event.target.checked })} />
                    <Input aria-label={`${module.label} label`} className="h-8 max-w-64" value={module.label}
                      onChange={(event) => updateMoreModule(module.key, { label: event.target.value })} />
                    <span className="hidden sm:inline text-xs text-muted-foreground font-mono">{module.key}</span>
                    <div className="flex-1" />
                    <button type="button" aria-label={`Move ${module.label} up`} disabled={index === 0} onClick={() => moveMoreModule(module.key, -1)} className="p-1.5 rounded hover:bg-accent disabled:opacity-40">↑</button>
                    <button type="button" aria-label={`Move ${module.label} down`} disabled={index === list.length - 1} onClick={() => moveMoreModule(module.key, 1)} className="p-1.5 rounded hover:bg-accent disabled:opacity-40">↓</button>
                  </div>
                ))}
                <div className="flex justify-end">
                  <Button data-testid="save-more-modules" onClick={() => saveWorkspaceConfig("modules", workspaceConfig.modules)} className="bg-primary hover:bg-primary-hover">Save Modules</Button>
                </div>
              </Card>
            )}
          </TabsContent>
        )}

        {isOwner && (
          <TabsContent value="roles">
            <div className="grid lg:grid-cols-2 gap-4 items-start">
              <Card className="p-5" data-testid="default-roles-card">
                <h3 className="font-heading font-semibold mb-3">Master Permission Map</h3>
                {(roles?.default_roles || []).map((r) => (
                  <div key={r.name} className="border-b last:border-0 py-2.5 text-sm">
                    <div className="font-medium capitalize">{r.name.replace("_", " ")}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{Object.entries(r.permissions).map(([m, a]) => `${m}: ${a.join("/")}`).join(" · ")}</div>
                  </div>
                ))}
              </Card>
              <Card className="p-5" data-testid="custom-role-card">
                <h3 className="font-heading font-semibold mb-3">Create Custom Role</h3>
                <Input placeholder='Role name, e.g. "Regional Manager"' value={roleName} onChange={(e) => setRoleName(e.target.value)} data-testid="custom-role-name" className="mb-3" />
                <div className="space-y-1.5 max-h-72 overflow-y-auto">
                  {MODULES_LIST.map((m) => (
                    <div key={m} className="flex items-center gap-1.5 text-xs flex-wrap">
                      <span className="w-24 capitalize font-medium shrink-0">{m}</span>
                      {ACTIONS_LIST.map((a) => (
                        <button type="button" key={a} data-testid={`perm-${m}-${a}`} onClick={() => togglePerm(m, a)}
                          className={`px-2 py-1 rounded border transition-colors ${perms[m]?.includes(a) ? "bg-primary text-inverse border-primary" : "hover:bg-accent"}`}>{a}</button>
                      ))}
                    </div>
                  ))}
                </div>
                <Button onClick={createRole} disabled={!roleName.trim()} className="mt-4 w-full bg-primary hover:bg-primary-hover" data-testid="create-role-button">Create Role</Button>
                {(roles?.custom_roles || []).length > 0 && (
                  <div className="mt-3 text-xs text-muted-foreground">Custom roles: {roles.custom_roles.map((r) => r.name).join(", ")}</div>
                )}
              </Card>
            </div>
          </TabsContent>
        )}

        {canViewAudit && (
          <TabsContent value="audit">
            <Card className="overflow-hidden" data-testid="audit-log-card">
              <table className="w-full text-sm">
                <thead className="bg-muted/60">
                  <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                    <th className="px-4 py-3">When</th><th className="px-4 py-3">Action</th>
                    <th className="px-4 py-3">Entity</th><th className="px-4 py-3">Detail (old → new)</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.map((a) => (
                    <tr key={a.id} className="border-t hover:bg-accent/40 transition-colors" data-testid={`audit-row-${a.id}`}>
                      <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{fmtDateTime(a.created_at)}</td>
                      <td className="px-4 py-2.5"><span className="text-xs px-2 py-0.5 rounded-full border bg-primary text-primary border-primary">{a.action}</span></td>
                      <td className="px-4 py-2.5 text-xs">{a.entity}</td>
                      <td className="px-4 py-2.5 text-xs text-muted-foreground font-mono max-w-[440px] truncate" title={a.detail}>{a.detail}</td>
                    </tr>
                  ))}
                  {!audit.length && <tr><td colSpan={4} className="px-4 py-10 text-center text-muted-foreground">No audit entries yet</td></tr>}
                </tbody>
              </table>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
